import { ApiError } from './api';
import { getToken } from './auth';

/** Save a value as a pretty-printed .json file via a transient object URL. */
export function downloadJson(filename: string, data: unknown): void {
  // Writing a file containing the four bytes `null` is never what the caller
  // meant, and the failure only surfaces later when someone tries to import it.
  if (data === null || data === undefined) {
    throw new Error('Nothing to download — the server returned no document');
  }
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.json') ? filename : `${filename}.json`;
  a.click();
  // Revoking immediately can cancel the download in some browsers; one tick is
  // enough for the click to have been dispatched.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** "Solar Install Agreement" → "solar-install-agreement" (safe in a filename). */
export function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'template'
  );
}

// ---- authenticated binary downloads -------------------------------------
// Artifact routes take the bearer token in a header, so a plain <a href> can't
// fetch them; pull the bytes with fetch and hand the browser a blob instead.

export async function fetchBlobWithAuth(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const token = getToken();
  const res = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = undefined;
    }
    throw new ApiError(res.status, detail);
  }
  const match = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '');
  return { blob: await res.blob(), filename: match?.[1] ?? null };
}

export function saveBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Download `path` (bearer-authenticated), naming the file from the server's header when it sends one. */
export async function downloadWithAuth(path: string, fallbackName: string): Promise<void> {
  const { blob, filename } = await fetchBlobWithAuth(path);
  saveBlob(filename ?? fallbackName, blob);
}
